// CrmEventFilters — панель CRM-фильтров календаря (R21.1):
// select «Компания», select «Контакт», живой поиск по встрече/лиду, кнопка сброса.
// Поиск передаётся наверх без debounce — debounce (300ms) живёт в CalendarPage.
import { Stack, TextField, MenuItem, Button } from '@mui/material';
import { useCompanyMap } from '@/api/companies';
import { useContactMap } from '@/api/contacts';

/** Значение CRM-фильтров календаря. */
export interface CrmEventFiltersValue {
  /** FK → companies.id либо null (без фильтра). */
  companyId: string | null;
  /** FK → contacts.id либо null (без фильтра). */
  contactId: string | null;
  /** Строка живого поиска (сырая, без debounce). */
  search: string;
}

interface CrmEventFiltersProps {
  /** Текущее значение фильтров. */
  value: CrmEventFiltersValue;
  /** Колбэк изменения любого фильтра. */
  onChange: (next: CrmEventFiltersValue) => void;
  /** Колбэк сброса всех фильтров к дефолту. */
  onReset: () => void;
}

/** Панель CRM-фильтров для календаря (компания/контакт/поиск/сброс). */
export function CrmEventFilters({ value, onChange, onReset }: CrmEventFiltersProps): JSX.Element {
  const companyMap = useCompanyMap();
  const contactMap = useContactMap();

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ md: 'center' }}
      sx={{ mb: 2 }}
      data-testid="crm-event-filters"
    >
      <TextField
        select
        size="small"
        label="Компания"
        value={value.companyId ?? ''}
        onChange={(e) =>
          onChange({ ...value, companyId: e.target.value === '' ? null : e.target.value })
        }
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">Все компании</MenuItem>
        {Array.from(companyMap?.entries() ?? []).map(([id, name]) => (
          <MenuItem key={id} value={id}>
            {name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Контакт"
        value={value.contactId ?? ''}
        onChange={(e) =>
          onChange({ ...value, contactId: e.target.value === '' ? null : e.target.value })
        }
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">Все контакты</MenuItem>
        {Array.from(contactMap?.entries() ?? []).map(([id, name]) => (
          <MenuItem key={id} value={id}>
            {name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Поиск по встрече или лиду"
        value={value.search}
        onChange={(e) => onChange({ ...value, search: e.target.value })}
        sx={{ minWidth: 240 }}
        inputProps={{ 'aria-label': 'Поиск по встрече или лиду' }}
      />
      <Button size="small" onClick={onReset}>
        Сбросить фильтры
      </Button>
    </Stack>
  );
}
